import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  SurveyRequest,
  SurveyNoTargetingResponse,
  UpdateSurveyStatus,
  BuyerSurveyResponse,
  SupplierResponseInterface,
  GetSupplierBlendInterface,
  PatchSurveyInterface,
  UpdateQuotaFieldsInterface,
  UpdateSurveyFieldsApiResponse,
  Quotas,
  TcResponse,
  CreateTcResponse,
  CreateTcPayload,
  PsidsBuyerUploadListResponse,
  ClearPsidsResponse,
  PsidsUploadRefListResponse,
  BuyerSurveyCountResponse,
  removeTcResponse,
  SurveyGroup,
  PSIDList,
  PSIDCount,
  PutSupplierObjectInterface,
  HighCpiConfrmationResponse,
} from '../../interfaces/survey.interface';

import { SurveyStatus } from '../../utils/survey-status';
import {
  IncludePostPayload,
  SurveyPayload,
  IncludePsidApiResponse,
} from '../../interfaces/survey-meta-data.interface';
import { EnvironmentConfig } from '../../interfaces/environment.interface';
import { V2SurveysQualification } from '../../interfaces/qualification.interface';
import { map } from 'rxjs/operators';
import { UpdateQuotaInterface } from '../../interfaces/manage-quotas.interface';
import {
  RemainingHours,
  SurveyHealthResponseInterface,
} from '../../../manage-survey-tabs/interfaces/manage-health-checks.interface';
import {
  McSpvSurveyNoTargetingResponse,
  McSpvSurveyRequest,
} from '../../../create-surveys/shared/ui/multicountry/interface/multi-country.interface';

import { LocationResItem } from '../../../shared/interfaces/create-surveys.interface';
import {
  UnicornPayload,
  UnicornResponse,
} from '../../interfaces/unicorn.interface';
@Injectable({
  providedIn: 'root',
})
export class BuyerApiService {
  private readonly _baseUrl = this._env.environment.pureSpectrum.url;
  private readonly _priceUrl =
    this._env.environment.pureSpectrum.surveyStatiscticsServiceUrl;

  constructor(
    private _http: HttpClient,
    @Inject('ENV') private _env: EnvironmentConfig
  ) {}

  addSurvey(surveyData: SurveyRequest) {
    return this._http.post<SurveyNoTargetingResponse>(
      `${this._baseUrl}/buyers/v2/surveys?UI=1`,
      surveyData,
      { headers: new HttpHeaders({ timeout: '60000' }) }
    );
  }

  updateMcQbSurvey(payload: McSpvSurveyRequest, surveyId: number) {
    return this._http.put<SurveyNoTargetingResponse>(
      `${this._baseUrl}/buyers/v2/projects/${surveyId}?UI=1&targeting=0`,
      payload
    );
  }

  updateSurvey(surveyData: SurveyPayload, surveyId: number) {
    return this._http.put<SurveyNoTargetingResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}?UI=1&targeting=0`,
      surveyData
    );
  }

  updateSurveyStatus(surveyId: number, statusObj: UpdateSurveyStatus) {
    return this._http.put<UpdateSurveyStatus>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/status?UI=1`,
      statusObj
    );
  }

  getSurveyHighCpiDetails(surveyId: number) {
    return this._http.get<HighCpiConfrmationResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/high-cpi-details`
    );
  }

  getSurveyById(surveyId: number, accessible?: boolean) {
    const paramObj = {
      UI: 1,
      QBS: 1,
      ...(accessible && { accessible }),
    };
    const params = new HttpParams({ fromObject: paramObj });
    return this._http.get<BuyerSurveyResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}`,
      { params }
    );
  }

  getBuyerUploadedList(surveyId: number, incl_excl: number) {
    return this._http.get<PsidsBuyerUploadListResponse>(
      `${this._baseUrl}/inclexcl/getBuyerUploadedList/${surveyId}?incl_excl=${incl_excl}`
    );
  }

  clearUploadedPSIDs(surveyId: number) {
    return this._http.delete<ClearPsidsResponse>(
      `${this._baseUrl}/inclexcl/clearUploadedPSIDs/${surveyId}`
    );
  }

  uploadPSIDRefFile(surveyId: number, fdata: FormData) {
    return this._http.post<PsidsUploadRefListResponse>(
      `${this._baseUrl}/inclexcl/uploadPSIDListFromUI/${surveyId}`,
      fdata
    );
  }

  getSupplierBlends(locale: string | null) {
    const options = locale
      ? { params: new HttpParams().set('locale', locale) }
      : {};
    return this._http.get<GetSupplierBlendInterface[]>(
      `${this._baseUrl}/buyers/v2/supplier-blends`,
      options
    );
  }

  getSurveyCounts(
    status = SurveyStatus.Live,
    page = 1,
    search = '',
    buyerId?: number,
    projectManagerId?: number,
    buyerProjectManagerId?: number
  ) {
    let url = `${this._baseUrl}/buyers/v2/surveys/count?UI=1&page=${page}&search=${search}&status=${status}&limit=20&targeting=0`;
    if (buyerId) {
      url = `${url}&buyerId=${buyerId}`;
    }
    if (projectManagerId) {
      url = `${url}&projectManagerId=${projectManagerId}`;
    }
    if (buyerProjectManagerId) {
      url = `${url}&buyerProjectManagerId=${buyerProjectManagerId}`;
    }
    return this._http.get<BuyerSurveyCountResponse>(url);
  }

  getSurveys(
    status: string | number = SurveyStatus.Live,
    page: number = 1,
    search = '',
    mcUniqueId: string = '',
    targeting: number = 0,
    buyerId?: number,
    projectManagerId?: number,
    buyerProjectManagerId?: number,
    searchPo: number = 0
  ) {
    let url = `${this._baseUrl}/buyers/v2/surveys?UI=1&page=${page}&search=${search}&status=${status}&targeting=${targeting}`;
    if (mcUniqueId) {
      url = url + `&mcUniqueId=${mcUniqueId}`;
    } else {
      url = url + `&limit=20`;
    }
    if (buyerId) {
      url = `${url}&buyerId=${buyerId}`;
    }
    if (projectManagerId) {
      url = `${url}&projectManagerId=${projectManagerId}`;
    }
    if (buyerProjectManagerId) {
      url = `${url}&buyerProjectManagerId=${buyerProjectManagerId}`;
    }
    return this._http.get<BuyerSurveyResponse[]>(url);
  }

  serachBuyerSurveys(
    status: number,
    billingPo: string,
    buyerId: number,
    strict: boolean = false
  ) {
    let url = `${this._baseUrl}/buyers/v2/surveys?UI=1&status=${status}&targeting=0&searchPo=${billingPo}&buyerId=${buyerId}`;
    if (strict) {
      url = `${url}&strict=${strict}`;
    }
    return this._http.get<BuyerSurveyResponse[]>(url);
  }

  getSurvey(surveyId?: number, targeting: number = 0) {
    return this._http.get<BuyerSurveyResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}?UI=1&targeting=${targeting}`
    );
  }

  getTrafficChannelData(
    surveyId: number,
    isDashboard: boolean = false,
    dashbordTabStatus: string = '',
    isMultiCountryWidget: boolean = false
  ) {
    let params = new HttpParams();
    params = params.set('UI', 1);
    if (isDashboard && isMultiCountryWidget) {
      params = params.set('dashboard', 1);
      params = params.set('status', this._getStatusParam(dashbordTabStatus));
    }
    return this._http.get<TcResponse[]>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/channels?${params}`
    );
  }

  createTrafficChannelSurvey(surveyId: number, payload: CreateTcPayload) {
    return this._http.post<CreateTcResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/channels?UI=1`,
      payload
    );
  }

  getQuotaDetails(
    surveyId?: number,
    queryParams?: {
      page: number;
      limit: number;
      search: string;
      trafficChannel?: string;
      psEntDtRange?: string;
    }
  ) {
    let url = `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas`;
    let params = new HttpParams();
    params = params.set('UI', 1);
    params = params.set('QBS', 1);
    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        (queryParams as any)[key] &&
          (params = params.set(key, (queryParams as any)[key]));
      });
    }
    return this._http.get<Quotas[]>(url, { observe: 'response', params }).pipe(
      map((response) => {
        return {
          header: response.headers.get('total'),
          quota: response.body,
        };
      })
    );
  }

  getSuppliersParams(
    mode: string,
    loi?: number,
    ir?: number,
    lang?: number | string,
    manualScpiOverrideEnabled?: boolean,
    fieldTime?: number | string,
    completesRequired?: number | string,
    supplierSummary?: boolean
  ) {
    let params = new HttpParams();
    params = params.set('stats', 1);
    params = params.set('mode', mode);
    if (loi) {
      params = params.set('LOI', loi);
    }
    if (ir) {
      params = params.set('IR', ir);
    }
    if (lang) {
      params = params.set('LNG', lang);
    }
    if (fieldTime) {
      params = params.set('fieldTime', fieldTime);
    }
    if (completesRequired) {
      params = params.set('goal', completesRequired);
    }
    if (manualScpiOverrideEnabled) {
      params = params.set(
        'manual_scpi_override_enabled',
        manualScpiOverrideEnabled
      );
    }
    if (supplierSummary) {
      params = params.set('supplierSummary', 1);
    }
    params = params.set('epc', 1);
    params = params.set('UI', 1);
    return params;
  }

  getSuppliers(
    surveyId: number,
    mode: string,
    loi?: number,
    ir?: number,
    lang?: number | string,
    fieldTime?: number | string,
    completesRequired?: number | string,
    supplierSummary?: boolean
  ) {
    const params = this.getSuppliersParams(
      mode,
      loi,
      ir,
      lang,
      undefined,
      fieldTime,
      completesRequired,
      supplierSummary
    );
    return this._http.get<SupplierResponseInterface[]>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/suppliers?${params}`
    );
  }

  putSuppliers(
    surveyId: number,
    mode: string,
    suppliers: PutSupplierObjectInterface[],
    priceOverride: number | string,
    manualOverride: boolean,
    surveyPauseThreshold?: number,
    loi?: number,
    ir?: number,
    lang?: number | string,
    blendId: string = 'NA',
    manualScpiOverrideEnabled?: boolean,
    isPurePriceRateCardEnable?: boolean,
    fieldTime?: number | string,
    defaultPurePriceOverride: boolean = false
  ) {
    let params = this.getSuppliersParams(
      mode,
      loi,
      ir,
      lang,
      undefined,
      fieldTime
    );
    params = params.set('manual_override', manualOverride);
    params = params.set('price_override', priceOverride ? priceOverride : '');
    if (blendId != 'NA') {
      params = params.set('blend_id', blendId);
    }
    if (!priceOverride && !manualOverride && surveyPauseThreshold) {
      params = params.set('survey_pause_threshold', surveyPauseThreshold);
    }
    if (manualScpiOverrideEnabled) {
      params = params.set(
        'manual_scpi_override_enabled',
        manualScpiOverrideEnabled
      );
    }

    params = params.set(
      'default_pure_price_override',
      !!defaultPurePriceOverride
    );

    params = params.set(
      'is_pure_price_rate_card_enable',
      !!isPurePriceRateCardEnable
    );

    return this._http.put<SupplierResponseInterface[]>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/suppliers?${params}`,
      suppliers
    );
  }

  getQualifications(surveyId: number | undefined) {
    return this._http.get<V2SurveysQualification[]>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/qualifications?UI=1`
    );
  }

  updateQuotaData(
    surveyId: number,
    quotaId: string,
    payload: UpdateQuotaFieldsInterface[]
  ) {
    return this._http.patch<UpdateQuotaInterface>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas/${quotaId}`,
      payload
    );
  }

  updateSupplierQuotaData(
    surveyId: number,
    quotaId: string,
    supplierId: string | number,
    payload: UpdateQuotaFieldsInterface[]
  ) {
    return this._http.patch<UpdateQuotaInterface>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas/${quotaId}/suppliers/${supplierId}`,
      payload
    );
  }

  cloneSurvey(surveyId: number, isServiceOperatorCreation?: boolean) {
    const params = this._getCloneSurveyParams(!!isServiceOperatorCreation);
    return this._http.post<BuyerSurveyResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/clone?${params}`,
      {}
    );
  }

  private _getCloneSurveyParams(isServiceOperatorCreation: boolean) {
    let params = new HttpParams();
    params = params.set('UI', '1');
    params = params.set('targeting', '0');
    if (isServiceOperatorCreation) {
      params = params.set('serviceOperatorCreation', '1');
    }
    return params;
  }

  updateSurveyField(surveyId: number, payload: UpdateQuotaFieldsInterface[]) {
    return this._http.patch<UpdateSurveyFieldsApiResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}`,
      payload
    );
  }
  deleteDraftSurvey(surveyId: number) {
    return this._http.delete(`${this._baseUrl}/buyers/v2/surveys/${surveyId}`);
  }

  patchSurvey(surveyId: number, payload: PatchSurveyInterface[]) {
    return this._http.patch<any>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}`,
      payload
    );
  }

  updateTMCpi(surveyId: number, cpiCurrent: number) {
    return this._http.put<SupplierResponseInterface[]>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/offerPrice?UI=1`,
      { value: cpiCurrent, margin_override: '' }
    );
  }

  updateOfferPriceOfSurvey(surveyId: number, cpiCurrent: number) {
    return this._http.put<SupplierResponseInterface[]>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/offerPrice?UI=1`,
      { value: cpiCurrent }
    );
  }

  getMarginReportData(surveyId: number | undefined, mode: string) {
    return this._http.get(
      `${this._baseUrl}/reports/surveys/${surveyId}/margin-report?mode=${mode}`
    );
  }

  removeTrafficChannelData(surveyId: number | undefined, payload: any) {
    return this._http.patch<removeTcResponse>(
      `${this._baseUrl}/buyers/v2/channels/${surveyId}/status`,
      payload
    );
  }

  getIncludePsids(surveyData: IncludePostPayload) {
    return this._http.post<IncludePsidApiResponse>(
      `${this._baseUrl}/buyers/v2/surveys/psids`,
      surveyData
    );
  }

  getGroupSurveys(
    startDate: string,
    endDate: string,
    locale = { languageCode: '', countryCode: '' },
    status = '',
    searchKey = '',
    searchValue = '',
    incl_excl = ''
  ) {
    return this.getSurveysLookup(
      startDate,
      endDate,
      locale,
      status,
      searchKey,
      searchValue,
      incl_excl
    );
  }

  getPSIDList(fromDate: string, toDate: string, surveyIds: number[] = []) {
    const payload = {
      fromDate,
      toDate,
      surveyIds,
    };
    return this._http.post<PSIDList>(
      `${this._baseUrl}/buyers/v2/surveys/psids`,
      payload
    );
  }

  getPSIDCounts(isExclusion: boolean, surveyIds: number[] = []) {
    const payload = {
      include_quotas_count: false, // No quotas count obj needed
      is_exclusion: isExclusion,
      survey_grouping: surveyIds,
    };
    return this._http.post<PSIDCount>(
      `${this._baseUrl}/buyers/v2/surveys/PSID/counts`,
      payload
    );
  }

  getSurveyHealths(surveyId: number) {
    return this._http.get<SurveyHealthResponseInterface[]>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/health`
    );
  }

  addPricingConfirmationLog(surveyId: number, confirmationMsg: string) {
    return this._http.post<BuyerSurveyResponse>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/pricing-confirmation-log?`,
      { pricing_confirmation_msg: confirmationMsg }
    );
  }

  public getSurveysLookup(
    startDate: string,
    endDate: string,
    { languageCode = '', countryCode = '' },
    status = '',
    searchKey = '',
    searchValue = '',
    incl_excl = '',
    billingNumbers: string[] = [],
    templateIds: number[] = [],
    projectManagerIds: number[] = []
  ) {
    const queryParamObj = {
      fromDate: startDate,
      toDate: endDate,
      ...(status && { status }),
      ...(searchKey && searchValue && { searchKey, searchValue }),
      ...(incl_excl && { incl_excl }),
      ...(billingNumbers.length && {
        billingNumbers: billingNumbers.join(','),
      }),
      ...(templateIds.length && { templateIds: templateIds.join(',') }),
      ...(projectManagerIds.length && {
        projectManagerIds: projectManagerIds.join(','),
      }),
      ...(countryCode && languageCode && { countryCode, languageCode }),
    };
    const params = new HttpParams({ fromObject: queryParamObj });
    return this._http.get<SurveyGroup[]>(
      `${this._baseUrl}/buyers/v2/surveys/lookup?${params}`
    );
  }

  public addMCSpvSurvey(payload: McSpvSurveyRequest) {
    const paramsObj = {
      UI: 1,
    };
    const params = new HttpParams({ fromObject: paramsObj });
    return this._http.post<McSpvSurveyNoTargetingResponse>(
      `${this._baseUrl}/buyers/v2/projects`,
      payload,
      { params }
    );
  }

  searchQualificationOptions(
    qualificationId: number,
    locale: string,
    searchText: string,
    excludeOptions: string
  ) {
    const queryParamObj = {
      locale,
      searchText,
      ...(excludeOptions && { exclude: excludeOptions }),
    };
    const params = new HttpParams({ fromObject: queryParamObj });
    return this._http.get<LocationResItem[]>(
      `${this._baseUrl}/buyers/v2/qualifications/${qualificationId}/lookup?${params}`
    );
  }

  findSurveyUnicorns(unicornPayload: UnicornPayload) {
    return this._http.post<UnicornResponse>(
      `${this._baseUrl}/buyers/v2/unicorn`,
      unicornPayload
    );
  }

  getSurveyRemainingHours(surveyId: number) {
    return this._http.get<RemainingHours>(
      `${this._priceUrl}/surveys/remaining-hours/${surveyId}`
    );
  }

  private _getStatusParam(dashbordTabStatus: string) {
    if (dashbordTabStatus === 'all') {
      const surveyStatus = Object.values(SurveyStatus).filter(
        (status) => typeof status === 'number'
      );
      return surveyStatus.join(',');
    }
    return dashbordTabStatus;
  }
}
